import { closeAllForms } from '../utils/closeallforms';
import { populateAmountUnitsDropdown } from '../utils/populateunits';
import { populateRateSetDropdown } from '../utils/populateratesetdropdown';
import { openForm } from '../utils/openform';
import { updateReferrerFields } from '../utils/updatereferrerfields';
import { getCSRFToken } from '../utils/getCSRFToken';
import { postJSONWithCSRFRetry } from '../utils/fetchWithCSRFRetry';
import { safeJsonParse } from '../utils/authErrorHandler';
import {
  formatDateISO,
  calculateDaysInclusive,
  addDays,
  formatDateDDMMYYYY,
  parseDate,
  getDateRange,
} from '../utils/dateUtils';
import { deepEqual } from '../utils/deepEqual';

export default function registerAddBookingForm() {
  // ADD NEW BOOKING (calendar date click)
  window.Alpine.store('addBookingForm', {
    dateClicked: '',
    startDate: '',
    endDate: '',
    fixedDateRange: '',
    showFixedDateRange: false,
    unavailableDates: [], // format should be ['23-01-2024']
    calendarId: null,
    product: 'Accommodation',
    dateSelectType: 'range',
    productId: null,
    soldAs: null,
    soldAsType: null,
    soldAsData: null,
    unitId: null,
    units: 1,
    totalUnits: 1,
    isPackage: false, // Track if selected rate set is a package

    // People options
    peopleAsOption: false,
    peoplePerUnit: 0, // Store the people per unit from RateSet
    amountPeopleOptions: [], // Store the generated options for amount_people
    selectedPeople: null, // Default selected option

    // Person type options (Adults/Children)
    usePersonTypes: false,
    adultOptions: [],
    childrenOptions: [],
    selectedAdults: 1,
    selectedChildren: 0,
    maxPersons: 0,

    // Tour duration selection
    tourDuration: 'single', // Default to single day for tours
    showTourDurationToggle: false, // Only show when rate set allows range

    price: 0,
    priceUpdated: false,
    priceLoading: false, // True when price is being calculated
    // Discount information
    discountAmount: 0,
    appliedDiscounts: [],
    flatpickrInstances: {},
    categorizedDates: {
      start: [],
      single: [],
      middle: [],
      end: [],
    },
    minMaxDays: {}, // Min/max days constraints per date
    closedToDays: {}, // Closed-to-arrival/departure flags from backend
    packageClosedToStartDates: [], // Package-only blocked start dates (admin-safe)
    packages: [], // Package info for dynamic availability checking

    // Validation state
    validationErrors: {
      peopleUnits: '',
      startEndDate: '',
    },
    validationTimer: null,

    // Submit message state
    submitMessage: '',
    messageType: '',
    submitMessageTimer: null,

    // Submission state - prevents double submissions
    isSubmitting: false,

    // Custom fields state
    selectedRateSet: null, // Store the full rate set object including active_custom_fields
    customFields: {}, // Track per-booking-item custom field values
    // Use an object instead of array for customFieldsPerPerson to avoid undefined access issues
    // Keys are person indices (0, 1, 2, etc.), values are objects with field values
    customFieldsPerPerson: {},

    // Safe getter for per-person custom fields - ALWAYS returns an object, never undefined
    // This prevents "Cannot read properties of undefined" errors during Alpine's reactive re-rendering
    getPersonFieldsObj(personIndex) {
      if (!this.customFieldsPerPerson[personIndex]) {
        this.customFieldsPerPerson[personIndex] = {};
      }
      return this.customFieldsPerPerson[personIndex];
    },
    getPersonLabel(count = 2, lowercase = false) {
      const singular = (
        this.selectedRateSet?.person_label_singular || 'Person'
      ).trim();
      const plural = (
        this.selectedRateSet?.person_label_plural || 'People'
      ).trim();
      const label = Number(count) === 1 ? singular : plural;
      return lowercase ? label.toLowerCase() : label;
    },
    getPersonFieldLabel() {
      return this.getPersonLabel(2);
    },

    // Method to validate people vs units
    validatePeopleUnits() {
      console.log('FUNCTION CALLED: validatePeopleUnits');

      // Skip validation if people are not being selected separately from units
      if (!this.peopleAsOption) {
        console.log('Skipping validation - peopleAsOption is false');
        this.clearAllValidationErrors();
        return true;
      }

      let totalPeople = 0;

      if (this.usePersonTypes) {
        totalPeople =
          (parseInt(this.selectedAdults) || 0) +
          (parseInt(this.selectedChildren) || 0);
      } else {
        totalPeople = parseInt(this.selectedPeople) || 0;
      }

      console.log(
        'Validating - totalPeople:',
        totalPeople,
        'units:',
        this.units,
        'peopleAsOption:',
        this.peopleAsOption,
      );

      if (totalPeople < this.units) {
        // Get unit type name
        const unitTypeName = this.soldAsType || 'unit';
        const message = `Please select at least ${
          this.units
        } ${this.getPersonLabel(
          this.units,
          true,
        )} (minimum 1 ${this.getPersonLabel(1, true)} per ${unitTypeName}).`;

        // Use general validation error method with auto-dismiss
        this.setValidationError('peopleUnits', message);
        return false;
      } else {
        // Clear all validation errors immediately when validation passes
        this.clearAllValidationErrors();
        return true;
      }
    },
    // Hides the units field if there is only 1 unit available
    showUnitsField() {
      console.log(
        'showUnitsField called, units:',
        this.totalUnits,
        typeof this.totalUnits,
      );
      return Number(this.totalUnits) > 1;
    },

    // Method to set validation error with auto-dismiss
    setValidationError(field, message) {
      console.log(`❌ Setting validation error for ${field}:`, message);

      // Clear any existing timer
      if (this.validationTimer) {
        clearTimeout(this.validationTimer);
        this.validationTimer = null;
      }

      // Set the error
      this.validationErrors = {
        ...this.validationErrors,
        [field]: message,
      };
      // No timer: all warnings persist until validation passes
    },

    // Method to clear all validation errors
    clearAllValidationErrors() {
      console.log('🧹 Clearing all validation errors');

      // Clear timer
      if (this.validationTimer) {
        clearTimeout(this.validationTimer);
        this.validationTimer = null;
      }

      // Clear all errors by creating new object (forces Alpine reactivity)
      this.validationErrors = {
        peopleUnits: '',
        // Add other validation fields here as needed
      };

      console.log('✅ All validation errors cleared:', this.validationErrors);
    },

    // Method to set submit message with auto-dismiss
    setSubmitMessage(message, type = 'error') {
      console.log(`❌ Setting submit message (${type}):`, message);

      // Clear any existing timer
      if (this.submitMessageTimer) {
        clearTimeout(this.submitMessageTimer);
      }

      // Set the message
      this.submitMessage = message;
      this.messageType = type;

      // Auto-clear error messages after 4 seconds (but keep success messages longer)
      if (type === 'error') {
        this.submitMessageTimer = setTimeout(() => {
          console.log('🕐 Timer fired - clearing submit message');
          this.clearSubmitMessage();
        }, 4000);
      }
    },

    // Method to clear submit message
    clearSubmitMessage() {
      console.log('🧹 Clearing submit message');

      // Clear timer
      if (this.submitMessageTimer) {
        clearTimeout(this.submitMessageTimer);
        this.submitMessageTimer = null;
      }

      // Clear message
      this.submitMessage = '';
      this.messageType = '';
    },

    // Method to check if form is valid for submission
    isFormValid() {
      return this.validatePeopleUnits() && this.startDate && this.endDate;
    },

    // Method to validate email format
    validateEmail(email) {
      // RFC 5322 compliant email regex (simplified but comprehensive)
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    // When available date on calendar gets clicked, this opens the Add Booking Form and loads all relevant info
    async handleCalendarDateClick(e) {
      console.log('FUNCTION CALLED: handleCalendarDateClick');

      closeAllForms();
      this.resetStore();
      this.units = 1;
      this.price = 0;

      // Get calendar ID from the calendar store instead of relying on data attribute
      // This ensures it's always available even for unavailable dates
      const calendarStore = Alpine.store('calendarStore');
      this.calendarId =
        calendarStore.calendar_selected?.id ||
        e.target.getAttribute('data-calendar-id');
      this.productId = e.target.getAttribute('data-product-id');

      this.product = e.target.getAttribute('data-product');

      // Ensure we have a valid calendar ID before proceeding
      if (!this.calendarId) {
        console.error('No calendar ID available for form rendering');
        return;
      }

      await this.renderForm();

      if (this.flatpickrInstances[this.product]) {
        this.flatpickrInstances[this.product].clear();
        this.flatpickrInstances[this.product].set('disable', [() => true]);
      }

      const date = e.target.getAttribute('data-date');
      this.dateClicked = date;
      console.log('dateClicked:', date);

      const unitId = e.target.getAttribute('data-unit-id') || null;
      console.log('unitId:', unitId);

      // Validate that we have a unitId before making the API call
      if (!unitId) {
        console.error(
          'No unit ID found on clicked element. Check data-unit-id attribute.',
        );
        this.setSubmitMessage(
          'Unable to load booking form. Please refresh the page and try again.',
          'error',
        );
        return;
      }

      // OPTIMIZED: Fetch rate data on-demand from new lightweight endpoint
      // This reduces initial page load by only fetching rate data when calendar-date is clicked
      const rateSetData = await this.getRateSetDataOnDemand(unitId, date);
      console.log('rateSetData:', rateSetData);
      this.soldAsData = rateSetData;

      // Guard clause: if no rate sets are available, show message and return
      if (!rateSetData || rateSetData.length === 0) {
        console.warn(
          'No rate sets available for this date/unit. unitId:',
          unitId,
          'date:',
          date,
        );
        this.setSubmitMessage(
          'No listings available for this date. Please select a different date or check your rate set configuration.',
          'error',
        );
        return;
      }

      this.soldAs = rateSetData.length > 0 ? rateSetData[0].id : null;
      this.soldAsType = rateSetData.length > 0 ? rateSetData[0].type : null;
      this.isPackage =
        rateSetData.length > 0 ? rateSetData[0].is_package || false : false;
      const totalUnits =
        rateSetData.length > 0 ? rateSetData[0].units_count : null;
      console.log('totalUnits:', totalUnits);
      this.totalUnits = totalUnits;
      this.peopleAsOption =
        rateSetData.length > 0 ? rateSetData[0].people_as_option : null;

      if (this.peopleAsOption) {
        console.log('peopleAsOption is true, setting selectedPeople to 1');
        this.selectedPeople = 1;
        this.peoplePerUnit =
          rateSetData.length > 0 ? rateSetData[0].people_per_unit : null;
        console.log('peoplePerUnit:', this.peoplePerUnit);
        this.generateAmountPeopleOptions();
      }
      this.usePersonTypes =
        rateSetData.length > 0 ? rateSetData[0].use_person_types : null;
      console.log('usePersonTypes:', this.usePersonTypes);
      let formIdToOpen =
        this.product === 'Accommodation'
          ? 'addAccommodationForm'
          : 'addTourForm';
      const fieldData = {
        start_date: date,
        accommodation_unit_id: unitId,
        tour_unit_id: unitId,
      };

      const cartStore = Alpine.store('cartStore');
      cartStore.checkEditMode();
      // Open the form with slight delay that form can be rendered and open with smooth transition
      setTimeout(() => {
        openForm(formIdToOpen, 'open', fieldData, null, null);
        document.body.classList.add('slideform-open'); // This is to prevent the background from scrolling when the form is open
        populateRateSetDropdown(rateSetData);
        this.initializeAllFlatpickrs(rateSetData[0].date_select);
        this.handleRateChange(rateSetData[0], unitId);
        populateAmountUnitsDropdown(totalUnits, this.product);
        updateReferrerFields();
      }, 100);
    },
    async renderForm() {
      // Render the form on the DOM
      let formContainer;
      let addBookingForm;
      let url;

      if (this.product === 'Accommodation') {
        formContainer = document.getElementById(
          'add-accommodation-form-container',
        );
        addBookingForm = document.getElementById('addAccommodationForm');
        url = `/calendar/add-accommodation-form/${this.calendarId}/`;
      } else if (this.product === 'Tour') {
        formContainer = document.getElementById('add-tour-form-container');
        addBookingForm = document.getElementById('addTourForm');
        url = `/calendar/add-tour-form/${this.calendarId}/`;
      }

      if (addBookingForm) {
        // Form is already rendered, just open it
        return; // Exit the function early
      }

      // Fetch the product form from the server
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-CSRFToken': getCSRFToken(),
        },
      });

      // Use safe JSON parsing to handle auth errors gracefully
      const data = await safeJsonParse(response);
      // Update the form container with the fetched HTML
      formContainer.innerHTML = data.add_booking_form_html;
    },
    updateSelectedRate(flatpickrElement) {
      console.log('FUNCTION CALLED: updateSelectedRate');

      // Get currently selected RateSet
      const soldAsId = this.soldAs;
      const unitId = this.unitId;
      console.log('soldAs ID:', soldAsId);

      // Find the corresponding RateSet object from this.soldAsData
      const selectedRateSet = this.soldAsData.find(
        (soldAs) => soldAs.id === soldAsId,
      );

      // Store the full rate set object (includes active_custom_fields from RateSetSerializer)
      this.selectedRateSet = selectedRateSet;

      this.peopleAsOption = selectedRateSet.people_as_option;
      this.usePersonTypes = selectedRateSet.use_person_types;

      // Update the soldAsType when a different rate is selected
      this.soldAsType = selectedRateSet.type;

      // Track if selected rate set is a package
      this.isPackage = selectedRateSet.is_package || false;

      // Reset custom fields when rate set changes
      // Using object approach - getPersonFieldsObj() creates entries on demand
      this.customFields = {};
      this.customFieldsPerPerson = {};

      if (selectedRateSet) {
        console.log('Selected RateSet:', selectedRateSet);
        console.log('Custom Fields:', selectedRateSet.active_custom_fields);
        // Pass the entire RateSet object to handleRateChange
        this.handleRateChange(selectedRateSet, unitId, flatpickrElement);
      } else {
        console.error('Selected RateSet not found in soldAsData.');
      }
    },
    async handleRateChange(selectedRateSet, unitId, flatpickrElement) {
      console.log('FUNCTION CALLED: handleRateChange');
      console.log('selectedRateSet:', selectedRateSet);

      // Store the full rate set object (includes active_custom_fields)
      this.selectedRateSet = selectedRateSet;
      console.log(
        'Updated selectedRateSet in handleRateChange:',
        this.selectedRateSet,
      );

      // Store current dates before any changes
      const currentStartDate = this.startDate;
      const currentEndDate = this.endDate;

      this.peoplePerUnit = selectedRateSet.people_per_unit;
      console.log('peoplePerUnit set to:', this.peoplePerUnit);
      console.log('usePersonTypes:', this.usePersonTypes);
      console.log('soldAsType:', this.soldAsType);

      // Show tour duration toggle only for tours with range mode and not fixed
      this.showTourDurationToggle =
        this.product === 'Tour' &&
        selectedRateSet.date_select === 'range' &&
        !selectedRateSet.is_fixed;

      // Reset tour duration to single when showing toggle
      if (this.showTourDurationToggle) {
        this.tourDuration = 'single';
      }

      // Helper to check if unit type is a person type (Person, Adult, or Child)
      const isPersonUnitType =
        this.soldAsType === 'Person' ||
        this.soldAsType === 'Adult' ||
        this.soldAsType === 'Child';

      // Generate person type options if usePersonTypes is true (for any unit type)
      if (this.usePersonTypes) {
        console.log('Generating person type options');
        if (isPersonUnitType) {
          const totalUnits = parseInt(selectedRateSet.units_count);
          this.generatePersonTypeOptions(totalUnits);
          // Initialize with 1 adult by default for Person/Adult/Child unit types
          this.selectedAdults = 1;
          this.selectedChildren = 0;
          this.units = 1;
        } else {
          // Reset units to 1 when switching to a rate with usePersonTypes
          this.units = 1;
          // Initialize with 1 adult by default
          this.selectedAdults = 1;
          this.selectedChildren = 0;
          // For non-Person types, call without totalUnits
          this.generatePersonTypeOptions();
        }
      } else if (this.peoplePerUnit != null) {
        console.log('Generating amount people options');
        // Reset units to 1 when switching to a rate with peoplePerUnit
        this.units = 1;
        this.generateAmountPeopleOptions();
      } else {
        // For Person/Adult/Child types without usePersonTypes or peoplePerUnit,
        // default to 1 unit (1 person)
        console.log(
          'Setting default units to 1 for unit type:',
          this.soldAsType,
        );
        this.units = 1;
      }

      // customFieldsPerPerson is now an object - getPersonFieldsObj() creates entries on demand
      // No need to pre-initialize based on people count

      const totalUnits = selectedRateSet.units_count;
      populateAmountUnitsDropdown(totalUnits, this.product);

      // Check if current dates are available before clearing
      const datesAvailable = await this.checkDateAvailability(
        selectedRateSet.id,
        unitId,
      );
      this.resetDatesAndPrice(!datesAvailable);

      if (datesAvailable) {
        // Keep the current dates
        console.log('Selected dates are available, preserving them');
        // Restore the dates that were stored
        this.startDate = currentStartDate;
        this.endDate = currentEndDate;
        // Update price with preserved dates
        this.updatePrice();
      } else {
        console.log(
          'Selected dates NOT available or no dates selected, clearing dates',
        );
      }

      if (selectedRateSet.is_fixed) {
        // Logic to display a dropdown with available fixed date ranges
        this.showFixedDateRange = true;
        // Populate dropdown with options
        this.populateFixedDateRangeDropdown(this.product, selectedRateSet.id);
      } else {
        // Show Flatpickr for non-fixed soldAs
        this.showFixedDateRange = false;

        if (!datesAvailable) {
          // Reset Flatpickr dates here if needed
          this.resetFlatpickrDates(selectedRateSet.id);
          this.updateInitialDateForIdentifier(this.product, this.dateClicked);
        }

        // Determine the actual mode to use for flatpickr initialization
        let flatpickrMode = selectedRateSet.date_select || 'range';
        if (this.showTourDurationToggle) {
          // For tours with range capability, use the user's selection
          flatpickrMode = this.tourDuration === 'single' ? 'single' : 'range';
        }

        if (flatpickrElement) {
          this.initializeFlatpickr(
            this.product,
            flatpickrElement,
            flatpickrMode,
          );
        } else {
          // Initialize all Flatpickr elements if the specific one is not found
          this.initializeAllFlatpickrs(flatpickrMode);
        }
      }
    },
    initializeAllFlatpickrs(dateSelectMode) {
      console.log('FUNCTION CALLED: initializeAllFlatpickrs');
      Object.keys(this.flatpickrInstances).forEach((product) => {
        const instance = this.flatpickrInstances[product].element;
        if (instance) {
          this.initializeFlatpickr(product, instance, dateSelectMode);
        }
      });
    },
    async handleUnitChange() {
      console.log('FUNCTION CALLED: handleUnitChange');

      // Store current dates before any changes
      const currentStartDate = this.startDate;
      const currentEndDate = this.endDate;

      // Check for Person, Adult, or Child unit types
      const isPersonUnitType =
        this.soldAsType === 'Person' ||
        this.soldAsType === 'Adult' ||
        this.soldAsType === 'Child';

      if (this.usePersonTypes) {
        console.log('Generating person type options after unit change');
        if (isPersonUnitType) {
          // For Person/Adult/Child type, pass the new units value
          this.generatePersonTypeOptions(this.units);
        } else {
          // For other types, recalculate based on new units * peoplePerUnit
          this.generatePersonTypeOptions();
        }
      } else if (this.peoplePerUnit != null) {
        console.log('Generating amount people options');
        this.generateAmountPeopleOptions();
      }

      // Validate people vs units after unit change
      this.validatePeopleUnits();

      // Check if current dates are available before clearing
      const datesAvailable = await this.checkDateAvailability(
        this.soldAs,
        this.unitId,
      );
      this.resetDatesAndPrice(!datesAvailable);

      if (datesAvailable) {
        // Keep the current dates
        console.log('Selected dates are available, preserving them');
        // Restore the dates that were stored
        this.startDate = currentStartDate;
        this.endDate = currentEndDate;

        // Clear any date validation errors since dates are available
        this.clearValidationError('startEndDate');

        if (this.showFixedDateRange === true) {
          // Populate dropdown with options
          this.populateFixedDateRangeDropdown(this.product, this.soldAs);
        } else {
          // Update flatpickr availability without clearing dates
          const instance = this.flatpickrInstances[this.product];
          if (instance) {
            const currentYear = instance.currentYear;
            const currentMonth = instance.currentMonth + 1;
            this.fetchUnavailableDates(
              currentYear.toString(),
              currentMonth.toString(),
              instance,
            );
          }
          // Update price with preserved dates
          this.updatePrice();
        }
      } else {
        console.log(
          'Selected dates NOT available or no dates selected, clearing dates',
        );

        // Show warning if dates were cleared due to unavailability (not just missing dates)
        if (currentStartDate && currentEndDate) {
          const unitWord = this.units === 1 ? 'unit' : 'units';
          const dateInterval =
            currentStartDate === currentEndDate
              ? currentStartDate
              : `${currentStartDate} to ${currentEndDate}`;
          this.setValidationError(
            'startEndDate',
            `Selected dates ${dateInterval} not available for ${this.units} ${unitWord}`,
          );
        }

        if (this.showFixedDateRange === true) {
          // Populate dropdown with options
          this.populateFixedDateRangeDropdown(this.product, this.soldAs);
        } else {
          // Reset Flatpickr dates
          this.resetFlatpickrDates();
          this.updateInitialDateForIdentifier(this.product, this.dateClicked);
        }
      }
    },
    generateAmountPeopleOptions() {
      console.log('FUNCTION CALLED: generateAmountPeopleOptions');

      if (this.peoplePerUnit == null || this.units == null) {
        console.warn('peoplePerUnit or units is null, cannot generate options');
        this.amountPeopleOptions = [];
        return;
      }

      const maxPeople = this.peoplePerUnit * this.units;
      console.log('this.peoplePerUnit:', this.peoplePerUnit);
      console.log('this.units:', this.units);
      console.log('maxPeople:', maxPeople);

      this.amountPeopleOptions = Array.from(
        { length: maxPeople },
        (_, i) => i + 1,
      );

      if (this.peopleAsOption == true) {
        // Keep current selection if valid, otherwise reset to minimum
        if (!this.selectedPeople || this.selectedPeople > maxPeople) {
          this.selectedPeople = 1;
        }
      } else {
        this.selectedPeople = null;
      }
    },
    handleAmountPeopleChange() {
      console.log('FUNCTION CALLED: handleAmountPeopleChange');
      // customFieldsPerPerson is an object - getPersonFieldsObj() creates entries on demand
      // No need to pre-initialize

      // Validate people vs units after people change
      this.validatePeopleUnits();

      if (this.showFixedDateRange === true) {
        // Populate dropdown with options
        this.populateFixedDateRangeDropdown(this.product, this.soldAs);
      }
      // Don't reset dates for people changes - only update price
      this.updatePrice();
    },
    populateFixedDateRangeDropdown(product, soldAsId) {
      console.log('FUNCTION CALLED: populateFixedDateRangeDropdown');
      const csrfToken = getCSRFToken();
      fetch('/bookings_app/fixed-date-ranges/', {
        method: 'POST',
        body: JSON.stringify({
          soldAs: soldAsId,
          amountUnits: this.units,
          selectedPeople: this.selectedPeople,
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      })
        .then(async (response) => {
          const data = await safeJsonParse(response);
          return data;
        })
        .then((data) => {
          let dropdown = null;
          if (product === 'Accommodation') {
            dropdown = document.querySelector(
              ' #addAccommodationForm .fixedDateRangeDropdown',
            );
          } else {
            dropdown = document.querySelector(
              '#addTourForm .fixedDateRangeDropdown',
            );
          }

          // Check if dropdown is found
          if (!dropdown) {
            console.error('Dropdown not found for product:', product);
            return; // Exit if dropdown is not found
          }

          // Clear existing options
          dropdown.innerHTML = '';

          // Combine available and unavailable date ranges
          const allRanges = [
            ...data.available_fixed_date_ranges.map((range) => ({
              ...range,
              available: true,
            })),
            ...data.unavailable_fixed_date_ranges.map((range) => ({
              ...range,
              available: false,
            })),
          ];

          // Sort the combined array by start date and then by end date
          allRanges.sort((a, b) => {
            const startDateComparison =
              new Date(a.date.split('/').reverse().join('-')) -
              new Date(b.date.split('/').reverse().join('-'));
            if (startDateComparison !== 0) return startDateComparison;
            return (
              new Date(a.end_date.split('/').reverse().join('-')) -
              new Date(b.end_date.split('/').reverse().join('-'))
            );
          });

          // Create and append the first disabled option
          const firstOption = document.createElement('option');
          if (allRanges.length === 0) {
            firstOption.textContent = 'No dates available at this time';
          } else {
            firstOption.textContent = 'Select date';
          }
          firstOption.style.color = 'grey';
          firstOption.disabled = true;
          firstOption.selected = true; // Make this option selected by default
          dropdown.appendChild(firstOption);

          // Append sorted date ranges to the dropdown
          allRanges.forEach((range) => {
            const option = document.createElement('option');
            option.value = `${range.date}-${range.end_date}`;

            // Check if start and end dates are the same (single day)
            const isSingleDay = range.date === range.end_date;

            if (isSingleDay) {
              // For single day tours, show only one date
              option.textContent = `${range.date}${
                range.available ? '' : ' (unavailable)'
              }`;
            } else {
              // For multi-day tours, show date range
              option.textContent = `${range.date} to ${range.end_date} ${
                range.available ? '' : ' (unavailable)'
              }`;
            }

            option.setAttribute('data-start-date', range.date);
            option.setAttribute('data-end-date', range.end_date);
            if (!range.available) {
              option.disabled = true; // Disable the option if unavailable
              option.style.color = 'grey'; // Optionally style the unavailable option
            }
            dropdown.appendChild(option);
          });

          // Ensure "Select date" remains selected after populating options
          firstOption.selected = true; // Explicitly set the first option to selected again

          console.log('Fixed Rates:', data);
          // Optionally, reset the fixedDateRange if you want to force user selection after adding the first option
          this.fixedDateRange = '';
          this.updatePrice();
        })
        .catch((error) =>
          console.error('Error fetching fixed date ranges:', error),
        );
    },
    fixedDateSelection() {
      console.log('FUNCTION CALLED: fixedDateSelection');

      let dropdown = null;
      if (this.product === 'Accommodation') {
        dropdown = document.querySelector(
          ' #addAccommodationForm .fixedDateRangeDropdown',
        );
      } else {
        dropdown = document.querySelector(
          '#addTourForm .fixedDateRangeDropdown',
        );
      }
      const selectedOption = dropdown.options[dropdown.selectedIndex];
      if (selectedOption) {
        // Extract the dates from the option's attributes
        const startDateStr = selectedOption.getAttribute('data-start-date');
        const endDateStr = selectedOption.getAttribute('data-end-date');

        // Convert dates from DD/MM/YYYY to YYYY-MM-DD format
        const startDate = this.convertDateToISO(startDateStr);
        const endDate = this.convertDateToISO(endDateStr);

        // Update the store's date properties
        this.startDate = startDate;
        this.endDate = endDate;
        console.log(
          'Updated startDate and endDate:',
          this.startDate,
          this.endDate,
        );
        this.updatePrice();
        // Clear date warning if both dates are set
        if (this.startDate && this.endDate) {
          this.clearValidationError('startEndDate');
        }
      }
    },
    initializeFlatpickr(product, flatpickrElement, mode = 'range') {
      console.log('FUNCTION CALLED: initializeFlatpickr');
      //console.log('Initializing Flatpickr:', product, flatpickrElement, mode);
      this.flatpickrInstances[product] = flatpickr(flatpickrElement, {
        disableMobile: 'true',
        mode: mode,
        dateFormat: 'd/m/Y',
        showMonths:
          mode === 'range'
            ? window.matchMedia('(min-width: 769px)').matches
              ? 2
              : 1
            : 1, // Show two months on desktop, one on mobile for range selection

        onOpen: (selectedDates, dateStr, instance) => {
          // Add loading class immediately when opening
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.add('loading-availability');
          }

          instance.clear();
          // Reset all date restrictions
          instance.set('disable', []); // Clear all existing restrictions
          instance.set('minDate', null); // Clear any minimum date restriction
          instance.set('maxDate', null); // Clear any maximum date restriction
          instance.set('disable', [
            function () {
              return true;
            },
          ]);
          this.updateInitialDateForIdentifier(this.product, this.dateClicked);
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1;
          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
            this.startDate,
          );

          this.updateFlatpickrDates(product, this.startDate);
        },
        onChange: (selectedDates, dateStr, instance) => {
          this.startDate = selectedDates[0]
            ? formatDateISO(selectedDates[0]) // Timezone-safe YYYY-MM-DD format
            : '';
          this.endDate =
            mode === 'single'
              ? this.startDate
              : selectedDates[1]
              ? formatDateISO(selectedDates[1])
              : '';

          const startDate = selectedDates[0];

          // Enable nextMonth arrow
          if (instance && instance.nextMonthNav) {
            instance.nextMonthNav.classList.remove('flatpickr-disabled'); // Remove disabled class
            instance.nextMonthNav.setAttribute('aria-disabled', 'false'); // Reset accessibility attribute
            instance.nextMonthNav.onclick = null; // Re-enable navigation
          }

          // Re-enable the year selector
          if (instance && instance.currentYearElement) {
            instance.currentYearElement.removeAttribute('disabled'); // Re-enable the year selector
            instance.currentYearElement.classList.remove('flatpickr-disabled'); // Remove the disabled class
          }

          // Re-enable the month dropdown (if it exists)
          if (instance && instance.monthElements) {
            instance.monthElements.forEach((monthElement) => {
              monthElement.removeAttribute('disabled'); // Re-enable the month selector
              monthElement.classList.remove('flatpickr-disabled'); // Remove the disabled class
            });
          }

          if (selectedDates.length === 1) {
            // Disable all dates before the selected start date and restrict backward selection
            instance.set('disable', [
              function (date) {
                return date < startDate;
              },
            ]);

            // Update the minDate to prevent backward selection for end date
            instance.set('minDate', startDate);

            // Fetch unavailable dates again with the selected start date
            this.fetchUnavailableDates(
              startDate.getFullYear().toString(),
              (startDate.getMonth() + 1).toString(),
              instance,
              startDate,
            );

            // Ensure restrictions are updated
            this.updateFlatpickrDates(this.product, startDate);

            // Update price after first date if single date is selected
            if (mode === 'single') {
              this.updatePrice();
            }
          } else if (selectedDates.length === 2) {
            this.updatePrice();
          }

          if (this.startDate && this.endDate) {
            this.clearValidationError('startEndDate');
          }
        },
        onMonthChange: (selectedDates, dateStr, instance) => {
          // Add loading class when month changes
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.add('loading-availability');
          }

          // Disable all dates when month changes
          instance.set('disable', [
            function () {
              return true; // Disable all dates initially
            },
          ]);
          const startDate = selectedDates[0];
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1; // +1 because getMonth() is zero-indexed

          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
            startDate,
          );
        },
        onYearChange: (selectedDates, dateStr, instance) => {
          // Add loading class when year changes
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.add('loading-availability');
          }

          // Disable all dates when month changes
          instance.set('disable', [
            function () {
              return true; // Disable all dates initially
            },
          ]);
          const startDate = selectedDates[0];
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1; // Adjusted to be 1-indexed

          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
            startDate,
          );
        },
      });
    },
    updateFlatpickrDates(product, startDate = null) {
      console.log('FUNCTION CALLED: updateFlatpickrDates');
      const instance = this.flatpickrInstances[product];

      if (instance && instance.daysContainer) {
        // Disable unavailable dates plus package-blocked start dates in pre-start mode.
        let disabledDates = [...this.unavailableDates];
        if (!startDate) {
          disabledDates = [
            ...new Set([...disabledDates, ...this.packageClosedToStartDates]),
          ];
        }

        // Set only unavailable dates initially
        instance.set('disable', disabledDates);

        // Convert categorized_dates arrays into Set for efficient lookup
        this.categorizedDates = {
          start: new Set(this.categorizedDates.start),
          single: new Set(this.categorizedDates.single),
          single2: new Set(this.categorizedDates.single2),
          middle: new Set(this.categorizedDates.middle),
          end: new Set(this.categorizedDates.end),
        };

        const unavailableDateSet = new Set(this.unavailableDates);

        const currentMonth = instance.currentMonth; // Track the currently viewed month
        const currentYear = instance.currentYear;

        const dayElements =
          instance.daysContainer.querySelectorAll('.flatpickr-day');

        dayElements.forEach((dayElem) => {
          const ariaLabel = dayElem.getAttribute('aria-label');
          const dateStr = this.convertAriaLabelToDateStr(ariaLabel); // Convert to DD-MM-YYYY format
          const dateObj = new Date(ariaLabel); // Create Date object for comparison
          const isClosedToArrival =
            this.closedToDays[dateStr]?.ct_start || false;
          const isClosedToDeparture =
            this.closedToDays[dateStr]?.ct_end || false;

          // Extract the month and year of the current date
          const elemMonth = dateObj.getMonth(); // Month is 0-indexed
          const elemYear = dateObj.getFullYear();

          // Disable dates before the startDate, if startDate is selected, but only in the same month
          if (
            startDate &&
            dateObj < startDate &&
            elemMonth === currentMonth &&
            elemYear === currentYear
          ) {
            dayElem.classList.add('flatpickr-disabled');
            dayElem.removeAttribute('tabindex'); // Prevent focusing on the disabled date
            return; // Skip further checks for this date
          }

          // Apply custom styling based on categorized dates
          if (this.categorizedDates.start.has(dateStr)) {
            dayElem.classList.add('start-date');
          }
          if (this.categorizedDates.single.has(dateStr)) {
            dayElem.classList.add('single-date');
          }
          if (this.categorizedDates.single2.has(dateStr)) {
            dayElem.classList.add('single2-date');
          }
          if (this.categorizedDates.middle.has(dateStr)) {
            dayElem.classList.add('middle-date');
          }
          if (this.categorizedDates.end.has(dateStr)) {
            dayElem.classList.add('end-date');
          }

          // Initial State: When the picker opens
          if (!this.startDate) {
            if (this.packageClosedToStartDates.includes(dateStr)) {
              dayElem.removeAttribute('tabindex');
              dayElem.onclick = (e) => e.preventDefault();
            }

            // Respect backend closed-to-arrival flags for package/store parity.
            if (isClosedToArrival) {
              dayElem.classList.add('has-ct-arrival');
              dayElem.classList.add('flatpickr-disabled-start');
              dayElem.removeAttribute('tabindex');
              dayElem.onclick = (e) => e.preventDefault();
            }

            if (
              this.categorizedDates.single.has(dateStr) ||
              this.categorizedDates.start.has(dateStr)
            ) {
              dayElem.classList.add('has-ct-arrival');
              dayElem.classList.add('flatpickr-disabled-start');
              dayElem.removeAttribute('tabindex');
              dayElem.onclick = (e) => e.preventDefault(); // Prevent selection on click
              // Tooltip for closed-to-arrival
              dayElem.classList.add('has-tooltip');
              dayElem._tippyClosed = tippy(dayElem, {
                content: 'Closed to Arrival',
                theme: 'light-border',
                trigger: 'mouseenter focus',
              });
            }
          } else if (this.startDate) {
            // After Start Date is Selected: Handle closed-to-arrival and closed-to-departure

            // Ensure the categorized dates (start, single, middle, end) still have their classes
            if (this.categorizedDates.start.has(dateStr)) {
              dayElem.classList.add('start-date');
            }
            if (this.categorizedDates.single.has(dateStr)) {
              dayElem.classList.add('single-date');
            }
            if (this.categorizedDates.single2.has(dateStr)) {
              dayElem.classList.add('single2-date');
            }
            if (this.categorizedDates.middle.has(dateStr)) {
              dayElem.classList.add('middle-date');
            }
            if (this.categorizedDates.end.has(dateStr)) {
              dayElem.classList.add('end-date');
            }

            // Disable previous month arrow once startDate has been selected
            if (instance && instance.prevMonthNav) {
              instance.prevMonthNav.classList.add('flatpickr-disabled'); // Add a disabled class
              instance.prevMonthNav.setAttribute('aria-disabled', 'true'); // Set accessibility attribute
              instance.prevMonthNav.onclick = (e) => e.preventDefault(); // Disable navigation
            }

            // Enable the start and single dates for selection
            if (
              (this.categorizedDates.start.has(dateStr) ||
                this.categorizedDates.single.has(dateStr)) &&
              !unavailableDateSet.has(dateStr)
            ) {
              dayElem.classList.remove('flatpickr-disabled');
              dayElem.setAttribute('tabindex', '0');
            }

            // Respect backend closed-to-departure flags after a start date is selected.
            if (isClosedToDeparture) {
              dayElem.classList.add('has-ct-departure');
              dayElem.classList.add('flatpickr-disabled');
              dayElem.removeAttribute('tabindex');
              dayElem.onclick = (e) => e.preventDefault();
            }

            // Disable dates before the startDate, but only within the same month and year
            if (
              startDate &&
              dateObj < startDate &&
              elemMonth === currentMonth &&
              elemYear === currentYear
            ) {
              dayElem.classList.add('flatpickr-disabled');
              dayElem.removeAttribute('tabindex'); // Prevent focusing on the disabled date
              return; // Skip further checks for this date
            }

            // Do not infer an upper bound from categorized date classes in end-date mode.
            // Backend `unavailable_dates` is the source of truth for valid end dates.
          }
        });
      } else {
        console.error(`Flatpickr instance for '${product}' not found.`);
      }
    },
    fetchUnavailableDates(year, month, instance, startDate = null) {
      console.log('FUNCTION CALLED: fetchUnavailableDates');
      const csrfToken = getCSRFToken();

      // Add loading class to show pulsating animation
      if (instance && instance.calendarContainer) {
        instance.calendarContainer.classList.add('loading-availability');
      }

      // Check if we're using 2 months (range mode)
      // If tour duration toggle exists, use the user's choice; otherwise use RateSet's dateSelectType
      const isTwoMonths = this.showTourDurationToggle
        ? this.tourDuration !== 'single'
        : this.dateSelectType === 'range';

      let requestData = {
        year: year,
        month: month,
        soldAs: this.soldAs,
        unitId: null,
        amountUnits: this.units,
        selectedPeople: this.selectedPeople,
        two_month_mode: isTwoMonths,
        context: 'admin', // Identify this as Admin (skip min constraints)
      };

      // Add start_date_selected to requestData if startDate is provided
      if (startDate) {
        requestData.start_date_selected = formatDateISO(startDate);
      }

      // Disable date selection until availability has been fetched and updated
      this.disableFlatpickr(instance, true);

      fetch('/bookings_app/get-unavailable-dates/', {
        method: 'POST',
        body: JSON.stringify(requestData),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      })
        .then(async (response) => {
          const data = await safeJsonParse(response);
          return data;
        })
        .then((data) => {
          this.categorizedDates = data.categorized_dates;
          // Use backend unavailable_dates when present (contains complete package/rates constraints).
          this.unavailableDates =
            data.unavailable_dates || data.categorized_dates.middle;
          this.minMaxDays = data.min_max_days || {}; // Store min/max constraints
          this.closedToDays = data.closed_to_days || {};
          this.packageClosedToStartDates =
            data.package_closed_to_start_dates || [];
          this.packages = data.packages || []; // Store package info

          this.updateFlatpickrDates(this.product, startDate);

          // Package end-date constraints are validated in backend get-unavailable-dates.
          // Keep frontend as a pure renderer of backend unavailable_dates/closed_to_days.

          // Re-enable date selection now that availability has been updated
          this.disableFlatpickr(instance, false);

          // Remove loading class after data is loaded
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.remove('loading-availability');
          }
        })
        .catch((error) => {
          console.error('Error fetching unavailable dates:', error);

          // Remove loading class even on error
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.remove('loading-availability');
          }
        });
    },
    updatePrice() {
      console.log('FUNCTION CALLED: updatePrice');
      const csrfToken = getCSRFToken();

      // Show loading state
      this.priceLoading = true;

      if (this.soldAs && this.units) {
        const soldAsId = this.soldAs; // ID of the currently selected RateSet
        // Find the selected RateSet object from soldAsData
        const selectedRateSet = this.soldAsData.find((s) => s.id === soldAsId);
        console.log('selectedPeople:', this.selectedPeople);
        console.log('selectedAdults:', this.selectedAdults);
        console.log('selectedChildren:', this.selectedChildren);
        if (!selectedRateSet) {
          console.error('Selected RateSet not found');
          return;
        }

        let requestData;
        if (selectedRateSet.is_fixed === true) {
          console.log('FIXED INTERVALS');
          // Prepare the data to be sent
          requestData = {
            product: this.product,
            startDate: this.startDate,
            endDate: this.endDate,
            soldAs: this.soldAs,
            units: this.units,
            selectedPeople: this.selectedPeople,
          };
        } else {
          console.log('FLEXI DATES');
          // Prepare the data to be sent
          requestData = {
            product: this.product,
            startDate: this.startDate,
            soldAs: this.soldAs,
            units: this.units,
            selectedPeople: this.selectedPeople,
          };

          // Include endDate only if it's available
          if (this.endDate) {
            requestData.endDate = this.endDate;
          }
        }

        fetch('/bookings_app/calculate-price/', {
          // ADD functionality to retreive available dates from product (isavailable) and from rateset (hasrate)
          method: 'POST',
          body: JSON.stringify(requestData),
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
        })
          .then(async (response) => {
            const data = await safeJsonParse(response);
            return data;
          })
          .then((data) => {
            console.log('=== DISCOUNT DEBUG (Frontend - Add Form) ===');
            console.log('Response data:', data);
            console.log('Discount amount:', data.discount_amount);
            console.log('Applied discounts:', data.applied_discounts);

            this.price = data.price; // Update the price

            // Update discount information if present
            if (data.applied_discounts && data.applied_discounts.length > 0) {
              this.discountAmount = parseFloat(data.discount_amount) || 0;
              this.appliedDiscounts = data.applied_discounts;
              console.log('Updated appliedDiscounts:', this.appliedDiscounts);
            } else {
              this.discountAmount = 0;
              this.appliedDiscounts = [];
              console.log('Reset appliedDiscounts to empty array');
            }

            // Price loaded - hide loading state
            this.priceLoading = false;
            this.triggerPriceUpdateEffect();
          })
          .catch((error) => {
            console.error('Error fetching price:', error);
            this.priceLoading = false;
          });
      } else {
        console.log('Start date or soldAs is not set');
        this.priceLoading = false;
      }
    },
    async getRateSetData(productType, productId) {
      console.log(
        'FUNCTION CALLED: getRateSetData with productType:',
        productType,
        'productId:',
        productId,
      );

      try {
        // Fetch data from the backend using the product_type and product_id
        const response = await fetch(
          `/rates/rate-sets/${productType}/${productId}/`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(), // If CSRF token is needed for GET requests
            },
          },
        );

        // Use safe JSON parsing to handle auth errors gracefully
        const data = await safeJsonParse(response);

        // Check for success and return the rate sets
        if (data.success && data.rate_sets) {
          console.log('Rate Sets Data:', data.rate_sets);
          return data.rate_sets.map((rateSet) => ({
            ...rateSet,
            id: rateSet.id.toString(), // Ensure id is string if needed
            units_count: rateSet.units_count.toString(), // Ensure units_count is string if needed
          })); // Return the rate sets
        } else {
          throw new Error(data.error || 'Error fetching rate set data');
        }
      } catch (error) {
        console.error('Error:', error);
        return []; // Handle error by returning an empty array or null
      }
    },
    async getRateSetDataOnDemand(unitId, date) {
      console.log(
        'FUNCTION CALLED: getRateSetDataOnDemand with unitId:',
        unitId,
        'date:',
        date,
      );

      try {
        // OPTIMIZED: Fetch rate data on-demand from new lightweight endpoint
        // This reduces initial page load by only fetching rate data when calendar-date is clicked
        const url = `/calendar/api/unit-rates/${unitId}/${date}/`;
        console.log('Fetching rate data from:', url);

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
        });

        console.log('Response status:', response.status);

        if (!response.ok) {
          console.error(
            'API request failed with status:',
            response.status,
            response.statusText,
          );
          return [];
        }

        // Use safe JSON parsing to handle auth errors gracefully
        const data = await safeJsonParse(response);
        console.log('API response data:', data);

        // Check for success and return the rate sets
        if (data.rate_sets) {
          console.log('On-Demand Rate Sets Data:', data.rate_sets);
          // Transform the data to match the expected format (endpoint now returns correct format)
          return data.rate_sets.map((rateSet) => ({
            ...rateSet,
            id: rateSet.id.toString(),
            units_count: rateSet.units_count?.toString() || '1',
          }));
        } else {
          console.warn('No rate_sets in response:', data);
          throw new Error(data.error || 'Error fetching rate set data');
        }
      } catch (error) {
        console.error('Error fetching on-demand rate data:', error);
        return []; // Handle error by returning an empty array
      }
    },
    // HELPER METHODS
    updateInitialDateForIdentifier(product, isoDate) {
      console.log('FUNCTION CALLED: updateInitialDateForIdentifier');
      if (this.flatpickrInstances[product]) {
        const newDate = parseDate(isoDate);
        if (newDate) {
          this.flatpickrInstances[product].jumpToDate(newDate, true);
        }
      }
    },
    triggerPriceUpdateEffect() {
      console.log('FUNCTION CALLED: triggerPriceUpdateEffect');
      this.priceUpdated = true;

      // Trigger CSS animation for price value
      const priceInput = document.querySelector('#price');
      if (priceInput) {
        priceInput.classList.add('price-animating');
        setTimeout(() => {
          priceInput.classList.remove('price-animating');
        }, 1800); // Match animation duration
      }

      setTimeout(() => {
        this.priceUpdated = false;
      }, 1800); // Duration of the background color effect
    },
    disableFlatpickr(flatpickrInstance, disable) {
      console.log('FUNCTION CALLED: disableFlatpickr');
      // Disable or enable the input field
      flatpickrInstance.input.disabled = disable;

      // Disable or enable the prev/next navigation buttons, if they exist
      if (flatpickrInstance.prevMonthNav && flatpickrInstance.nextMonthNav) {
        flatpickrInstance.prevMonthNav.disabled = disable;
        flatpickrInstance.nextMonthNav.disabled = disable;
      }

      // Check if calendarContainer exists
      if (flatpickrInstance.calendarContainer) {
        // Optionally, disable the year and month dropdowns if present
        const pluginElements =
          flatpickrInstance.calendarContainer.querySelectorAll(
            '.flatpickr-monthDropdown-months, .numInput',
          );
        pluginElements.forEach((element) => {
          element.disabled = disable;
        });
      }
    },
    convertDateToISO(dateStr) {
      const parts = dateStr.split('/');
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    },
    convertAriaLabelToDateStr(ariaLabel) {
      const date = new Date(ariaLabel);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0'); // getMonth() is zero-indexed
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
    resetDatesAndPrice(clearDates = true) {
      console.log('FUNCTION CALLED: resetDatesAndPrice');

      if (clearDates) {
        // Clear dates
        this.startDate = '';
        this.endDate = '';

        // Reset fixed date range selection to the first option if needed
        this.fixedDateRange = '';
        const dropdown = document.querySelector('.fixedDateRangeDropdown');
        dropdown.innerHTML = ''; // Clear existing options
      }

      this.price = 0;
      // Clear discount information
      this.discountAmount = 0;
      this.appliedDiscounts = [];
    },
    async checkDateAvailability(soldAsId, unitId) {
      console.log('Checking date availability for currently selected dates...');
      console.log('Checking with units:', this.units);

      // If no dates selected yet, return false
      if (!this.startDate || !this.endDate) {
        return false;
      }

      const csrfToken = getCSRFToken();

      // startDate and endDate are already in YYYY-MM-DD format
      const startDateISO = this.startDate;
      const endDateISO = this.endDate;
      const startDateObj = parseDate(startDateISO);
      const endDateObj = parseDate(endDateISO);
      if (!startDateObj || !endDateObj) {
        return false;
      }

      const isTwoMonths = this.showTourDurationToggle
        ? this.tourDuration !== 'single'
        : this.dateSelectType === 'range';

      try {
        const response = await postJSONWithCSRFRetry(
          '/bookings_app/get-unavailable-dates/',
          {
            year: startDateObj.getFullYear().toString(),
            month: (startDateObj.getMonth() + 1).toString(),
            soldAs: soldAsId,
            unitId: unitId,
            amountUnits: this.units, // Pass the units count for availability check
            selectedPeople: this.selectedPeople,
            two_month_mode: isTwoMonths,
            context: 'admin',
          },
        );

        const data = await response.json();
        console.log('Availability check response:', data);
        const unavailableDates = new Set(
          data.unavailable_dates || data.categorized_dates?.middle || [],
        );
        const packageClosedToStartDates = new Set(
          data.package_closed_to_start_dates || [],
        );
        console.log(
          'Unavailable dates / package start-closed:',
          unavailableDates.size,
          packageClosedToStartDates.size,
        );

        // Check if any date in the range is unavailable
        const start = new Date(startDateObj);
        const end = new Date(endDateObj);

        // Helper function to convert YYYY-MM-DD to DD-MM-YYYY
        const formatDateDDMMYYYY = (isoDate) => {
          const [year, month, day] = isoDate.split('-');
          return `${day}-${month}-${year}`;
        };

        const datesToCheck = [];
        const current = new Date(start);
        while (current <= end) {
          const dateStr = formatDateISO(current);
          datesToCheck.push(dateStr);
          current.setDate(current.getDate() + 1);
        }
        console.log('Dates to check (YYYY-MM-DD):', datesToCheck);

        // Check each date against unavailable dates (converting format for comparison)
        const currentCheck = new Date(start);
        let isFirstDate = true;
        while (currentCheck <= end) {
          const dateStrISO = formatDateISO(currentCheck);
          const dateStrFormatted = formatDateDDMMYYYY(dateStrISO);

          if (unavailableDates.has(dateStrFormatted)) {
            console.log(
              `❌ Date ${dateStrISO} (${dateStrFormatted}) is unavailable for ${this.units} units`,
            );
            return false;
          }
          if (isFirstDate && packageClosedToStartDates.has(dateStrFormatted)) {
            console.log(
              `❌ Start date ${dateStrISO} (${dateStrFormatted}) blocked by required package component availability`,
            );
            return false;
          }
          isFirstDate = false;
          currentCheck.setDate(currentCheck.getDate() + 1);
        }

        // Re-check selected end date using backend start-date-aware validation.
        // This catches package constraints that only become explicit after a start date is chosen.
        const startAwareResponse = await postJSONWithCSRFRetry(
          '/bookings_app/get-unavailable-dates/',
          {
            year: startDateObj.getFullYear().toString(),
            month: (startDateObj.getMonth() + 1).toString(),
            soldAs: soldAsId,
            unitId: unitId,
            amountUnits: this.units,
            selectedPeople: this.selectedPeople,
            two_month_mode: isTwoMonths,
            context: 'admin',
            start_date_selected: startDateISO,
          },
        );
        const startAwareData = await startAwareResponse.json();
        const startAwareUnavailableDates = new Set(
          startAwareData.unavailable_dates ||
            startAwareData.categorized_dates?.middle ||
            [],
        );
        const selectedEndFormatted = formatDateDDMMYYYY(endDateISO);
        if (startAwareUnavailableDates.has(selectedEndFormatted)) {
          console.log(
            `❌ Selected end date ${endDateISO} (${selectedEndFormatted}) is unavailable for start ${startDateISO}`,
          );
          return false;
        }

        console.log(`✅ All dates are available for ${this.units} units`);
        return true;
      } catch (error) {
        console.error('Error checking date availability:', error);
        return false;
      }
    },
    resetFlatpickrDates(soldAsId) {
      console.log('FUNCTION CALLED: resetFlatpickrDates');

      // Ensure flatpickrInstances exist before clearing them
      Object.values(this.flatpickrInstances).forEach((flatpickrInstance) => {
        if (flatpickrInstance) {
          // Check if the instance exists
          flatpickrInstance.clear(); // Clears the selected dates
          // You can also set a default date here if needed
          // flatpickrInstance.setDate("2023-01-01", true); // Example to set a specific date
        } else {
          console.error('Flatpickr instance is undefined.');
        }
      });

      console.log('Flatpickr dates reset');
    },
    formatCurrency(amount, currency) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(parseFloat(amount));
    },
    closeForm() {
      document.body.classList.remove('slideform-open');
      closeAllForms();
      // Deactivate backdrop
      const backdrop = document.getElementById('backdrop');
      if (backdrop) {
        backdrop.style.display = 'none';
      }
      // Reset custom fields to fresh state (like a page reload)
      this.customFields = {};
      this.customFieldsPerPerson = {};
    },
    resetStore() {
      document.body.classList.remove('slideform-open');
      console.log('Resetting store to initial state');

      // Reset all variables to their initial values
      this.dateClicked = '';
      this.startDate = '';
      this.endDate = '';
      this.fixedDateRange = '';
      this.showFixedDateRange = false;
      this.unavailableDates = [];
      this.product = 'Accommodation';
      this.dateSelectType = 'range';
      this.productId = null;
      this.soldAs = null;
      this.soldAsType = null;
      this.soldAsData = null;
      this.unitId = null;
      this.units = 1;

      this.peopleAsOption = false;
      this.peoplePerUnit = 0;
      this.amountPeopleOptions = [];
      this.selectedPeople = null;

      // Reset tour duration properties
      this.tourDuration = 'single';
      this.showTourDurationToggle = false;

      this.price = 0;
      this.priceUpdated = false;
      this.priceLoading = false;
      // Reset discount information
      this.discountAmount = 0;
      this.appliedDiscounts = [];
      this.categorizedDates = {
        start: [],
        single: [],
        middle: [],
        end: [],
      };

      // Reset validation errors and submit messages
      this.clearAllValidationErrors();
      this.clearSubmitMessage();

      // Reset submission state
      this.isSubmitting = false;

      // Reset custom fields
      // IMPORTANT: Set selectedRateSet to null FIRST to prevent template rendering
      // while customFieldsPerPerson is being reset
      this.selectedRateSet = null;
      this.customFields = {};
      // Simple reset - getPersonFieldsObj() will create objects on demand
      this.customFieldsPerPerson = {};
    },
    validateForm(event, action) {
      // Prevent double submissions
      if (this.isSubmitting) {
        console.log(
          'Form submission already in progress, ignoring duplicate click',
        );
        event.preventDefault();
        return;
      }

      const form = event.target.closest('form');

      // Ensure the start and end dates are defined
      if (!this.startDate || !this.endDate) {
        event.preventDefault();
        this.setValidationError(
          'startEndDate',
          'Please select a valid start and end date.',
        );
        return;
      }

      // Validate people vs units
      if (!this.validatePeopleUnits()) {
        event.preventDefault();
        this.setSubmitMessage(this.validationErrors.peopleUnits, 'error');
        return;
      }

      // Add validation for person types (ensure at least one person is selected)
      if (this.usePersonTypes) {
        const totalPeople =
          (parseInt(this.selectedAdults) || 0) +
          (parseInt(this.selectedChildren) || 0);
        if (totalPeople === 0) {
          event.preventDefault();
          this.setSubmitMessage(
            'Please select at least one adult or child.',
            'error',
          );
          return;
        }
      } else if (
        this.peopleAsOption &&
        (!this.selectedPeople || this.selectedPeople === 0)
      ) {
        event.preventDefault();
        this.setSubmitMessage('Please select the number of people.', 'error');
        return;
      }

      // Validate email format
      const emailInput = form.querySelector(
        'input[type="email"], input[name="email"]',
      );
      if (emailInput && emailInput.value) {
        if (!this.validateEmail(emailInput.value)) {
          event.preventDefault();
          this.setSubmitMessage(
            'Please enter a valid email address (e.g., name@example.com)',
            'error',
          );
          // Also focus the email field to draw attention
          emailInput.focus();
          return;
        }
      }

      if (form.checkValidity()) {
        this.submitForm(event, action);
      } else {
        form.reportValidity();
      }
    },
    submitForm(event, action) {
      console.log('FORM SUBMITTED!! Action:', action);
      console.log('usePersonTypes:', this.usePersonTypes);
      console.log('selectedAdults:', this.selectedAdults);
      console.log('selectedChildren:', this.selectedChildren);

      // Set submitting flag to prevent duplicate submissions
      this.isSubmitting = true;

      // Double-check validation (in case submitForm is called directly)
      if (this.usePersonTypes) {
        const totalPeople =
          (parseInt(this.selectedAdults) || 0) +
          (parseInt(this.selectedChildren) || 0);
        if (totalPeople === 0) {
          console.error('Form submitted with no people selected');
          this.isSubmitting = false;
          return;
        }
      }

      document.body.classList.remove('slideform-open');
      this.closeForm();
      const calendarStore = Alpine.store('calendarStore');
      const cartStore = Alpine.store('cartStore');
      const toastStore = Alpine.store('toastStore');
      calendarStore.isLoading = true;
      let formElement = event.target.form || event.currentTarget.form;
      // Fallback: try to find form by ID if not found via event
      if (!formElement) {
        formElement = document.querySelector(
          '#addAccommodationForm form, #addTourForm form',
        );
      }
      if (!formElement) {
        console.error('Could not find form element for submission');
        return;
      }
      const formData = new FormData(formElement);
      formData.append('action', action);

      // Handle person type data based on unit type
      if (this.unitType === 'Person' && this.usePersonTypes) {
        formData.set('person_type', this.personType);
      } else if (this.usePersonTypes) {
        // Ensure adults and children are added if usePersonTypes is true
        const adults = this.selectedAdults || '0';
        const children = this.selectedChildren || '0';
        formData.set('amount_adults', adults);
        formData.set('amount_children', children);
        console.log(
          'Adding person types - adults:',
          adults,
          'children:',
          children,
        );
      }

      // Add custom field values to form data
      if (this.customFields && Object.keys(this.customFields).length > 0) {
        console.log('Adding custom fields:', this.customFields);
        Object.keys(this.customFields).forEach((fieldName) => {
          const value = this.customFields[fieldName];
          if (value !== null && value !== undefined && value !== '') {
            formData.set(`custom_field_${fieldName}`, value);
          }
        });
      }

      // Add per-person custom field values to form data
      // customFieldsPerPerson is now an object with numeric keys
      if (
        this.customFieldsPerPerson &&
        Object.keys(this.customFieldsPerPerson).length > 0
      ) {
        console.log(
          'Adding per-person custom fields:',
          this.customFieldsPerPerson,
        );
        Object.entries(this.customFieldsPerPerson).forEach(
          ([personIndex, personFields]) => {
            Object.keys(personFields).forEach((fieldName) => {
              const value = personFields[fieldName];
              if (value !== null && value !== undefined && value !== '') {
                formData.set(
                  `custom_field_${fieldName}_person_${personIndex}`,
                  value,
                );
              }
            });
          },
        );
      }

      // Also collect custom fields directly from form inputs (for adult/child fields that might not be in store)
      // This ensures fields inside Alpine.js x-if templates are included
      if (formElement) {
        const customFieldInputs = formElement.querySelectorAll(
          '[name^="custom_field_"]',
        );
        console.log(
          `Found ${customFieldInputs.length} custom field inputs in form`,
        );
        customFieldInputs.forEach((input) => {
          const name = input.name;
          let value = null;

          if (input.type === 'checkbox') {
            value = input.checked ? 'true' : '';
          } else if (input.type === 'radio') {
            if (input.checked) {
              value = input.value;
            } else {
              return; // Skip unchecked radio buttons
            }
          } else {
            value = input.value;
          }

          // Always add to FormData (will overwrite if already exists, which is fine)
          // Only skip if value is truly empty/null/undefined
          if (value !== null && value !== undefined && value !== '') {
            formData.set(name, value);
            console.log(
              `Added custom field from form input: ${name} = ${value}`,
            );
          } else {
            console.log(`Skipping empty custom field: ${name}`);
          }
        });
      } else {
        console.warn(
          'Form element not found, cannot collect custom fields from inputs',
        );
      }

      // Log all form data
      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`);
      }

      // Set flag BEFORE fetch to prevent race condition with WebSocket message
      calendarStore.skipNextWebSocketUpdate = true;

      fetch('/bookings_app/create_booking/', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRFToken': getCSRFToken(),
        },
      })
        .then(async (response) => {
          const data = await safeJsonParse(response);
          return { status: response.status, body: data };
        })
        .then(({ status, body }) => {
          if (status >= 400) throw body;
          console.log('Booking created successfully:', body);

          // Replace all booking items belonging to new booking and add new ones
          const newItemsMap = new Map(
            body.bookingItems.map((item) => [item.item.id, item]),
          );
          // Filter out any old items that are going to be updated
          let updatedBookingItems = calendarStore.bookingItems.filter(
            (item) => !newItemsMap.has(item.item.id),
          );
          // Then, add all new items (this adds both completely new items and updates existing ones)
          updatedBookingItems = updatedBookingItems.concat(body.bookingItems);
          // Finally, assign the updated list back to the store

          // Check if blocked dates have changed before updating
          const blockedDatesChanged = !deepEqual(
            calendarStore.calendarDatesBlocked,
            body.calendar_dates_blocked,
          );

          if (blockedDatesChanged) {
            toastStore.addToast(
              'Unit blocks have been updated due to linked availability',
              'info',
            );
          }

          // Update all calendar state simultaneously to prevent flickering
          // This ensures bookingItems and calendarDatesBlocked are updated together
          calendarStore.bookingItems = updatedBookingItems;
          calendarStore.calendarDatesBlocked = body.calendar_dates_blocked;
          // Clear filter cache to ensure new items are rendered
          calendarStore._bookingItemsCache = null;
          calendarStore._blockItemsCache = null;
          calendarStore.isLoading = false;
          cartStore.editModeBooking = body.edit_mode_booking;
          cartStore.bookingItemsCount = body.booking_items_count;
          //toastStore.addToast(body.message, 'success');

          const fields = document.getElementsByClassName('slideForm-field');
          Array.from(fields).forEach((field) => {
            field.value = '';
          });

          // Reset submitting flag after successful completion
          this.isSubmitting = false;
        })
        .catch((error) => {
          console.error('Error creating booking:', error);
          calendarStore.isLoading = false;
          // Reset submitting flag on error so user can retry
          this.isSubmitting = false;
          toastStore.addToast(
            error.message || 'An unexpected error occurred',
            'danger',
          );
        });
    },
    // New method to generate options for adults and children dropdowns
    generatePersonTypeOptions(totalUnits) {
      console.log('FUNCTION CALLED: generatePersonTypeOptions');

      // Check for Person, Adult, or Child unit types
      const isPersonUnitType =
        this.soldAsType === 'Person' ||
        this.soldAsType === 'Adult' ||
        this.soldAsType === 'Child';

      if (isPersonUnitType) {
        // For Person/Adult/Child type, use totalUnits as maxPersons
        this.maxPersons = parseInt(totalUnits) || 1;
        console.log(
          'Person unit type - maxPersons set to totalUnits:',
          this.maxPersons,
        );
      } else {
        // For other types, calculate based on units * peoplePerUnit
        if (this.peoplePerUnit == null || this.units == null) {
          console.warn(
            'peoplePerUnit or units is null, cannot generate options',
          );
          this.adultOptions = [0];
          this.childrenOptions = [0];
          return;
        }

        this.maxPersons = this.peoplePerUnit * this.units;
        console.log('Other type - maxPersons calculated:', {
          peoplePerUnit: this.peoplePerUnit,
          units: this.units,
          maxPersons: this.maxPersons,
        });
      }

      // Generate options from 0 to maxPersons (inclusive)
      this.adultOptions = [...Array(this.maxPersons + 1).keys()];
      this.childrenOptions = [...Array(this.maxPersons + 1).keys()];

      console.log('Generated options:', {
        adultOptions: this.adultOptions,
        childrenOptions: this.childrenOptions,
      });

      // Set default values
      this.selectedAdults = 0;
      this.selectedChildren = 0;
    },

    // Update options based on the other field's selection
    updatePersonTypeOptions() {
      console.log('FUNCTION CALLED: updatePersonTypeOptions');

      // Calculate remaining slots
      const remainingSlots =
        this.maxPersons -
        (parseInt(this.selectedAdults) + parseInt(this.selectedChildren));
      console.log('Remaining slots:', remainingSlots);

      // Update adults options if children changed
      if (this.selectedChildren !== null) {
        const maxAdults = this.maxPersons - parseInt(this.selectedChildren);
        this.adultOptions = [...Array(maxAdults + 1).keys()];
      }

      // Update children options if adults changed
      if (this.selectedAdults !== null) {
        const maxChildren = this.maxPersons - parseInt(this.selectedAdults);
        this.childrenOptions = [...Array(maxChildren + 1).keys()];
      }

      console.log('Updated options:', {
        adultOptions: this.adultOptions,
        childrenOptions: this.childrenOptions,
        selectedAdults: this.selectedAdults,
        selectedChildren: this.selectedChildren,
      });
    },

    // Handle changes to adult/child selections
    handlePersonTypeChange() {
      console.log('FUNCTION CALLED: handlePersonTypeChange');
      // Update both fields' options
      this.updatePersonTypeOptions();

      const totalPeople =
        parseInt(this.selectedAdults || 0) +
        parseInt(this.selectedChildren || 0);

      // Check for Person, Adult, or Child unit types
      const isPersonUnitType =
        this.soldAsType === 'Person' ||
        this.soldAsType === 'Adult' ||
        this.soldAsType === 'Child';

      if (isPersonUnitType) {
        // For Person/Adult/Child unit type, update units with the total
        this.units = totalPeople;
        console.log('Units updated:', {
          adults: this.selectedAdults,
          children: this.selectedChildren,
          totalUnits: this.units,
        });
        // Reset dates and price because this is changing units selection
        if (this.showFixedDateRange === true) {
          // Populate dropdown with options
          this.populateFixedDateRangeDropdown(this.product, this.soldAs);
        } else {
          // Reset Flatpickr dates
          this.resetFlatpickrDates();
          this.updateInitialDateForIdentifier(this.product, this.dateClicked);
        }
        this.updatePrice();
      } else {
        // For other unit types, update selectedPeople with the total
        this.selectedPeople = totalPeople;
        console.log('People updated:', {
          adults: this.selectedAdults,
          children: this.selectedChildren,
          totalPeople: this.selectedPeople,
        });
        if (this.showFixedDateRange === true) {
          // Populate dropdown with options
          this.populateFixedDateRangeDropdown(this.product, this.soldAs);
        }
        this.updatePrice();
      }

      // customFieldsPerPerson is an object - getPersonFieldsObj() creates entries on demand
      // No need to pre-initialize when person count changes

      // Validate people vs units after person type change
      this.validatePeopleUnits();
    },
    // Method to handle tour duration change
    handleTourDurationChange() {
      console.log('FUNCTION CALLED: handleTourDurationChange');

      // Reset dates when switching modes
      this.startDate = '';
      this.endDate = '';
      this.price = 0;

      // Determine the new mode
      const newMode = this.tourDuration === 'single' ? 'single' : 'range';

      // Use existing method to clear flatpickr dates
      this.resetFlatpickrDates();

      // Clear the input field visually as well
      if (this.flatpickrInstances[this.product]) {
        const element = this.flatpickrInstances[this.product].element;
        element.value = '';

        // Destroy current instance
        this.flatpickrInstances[this.product].destroy();

        // Reinitialize with new mode
        this.initializeFlatpickr(this.product, element, newMode);

        // Update initial date
        this.updateInitialDateForIdentifier(this.product, this.dateClicked);
      }
    },
    clearValidationError(field) {
      this.validationErrors = {
        ...this.validationErrors,
        [field]: '',
      };
    },
  });
}
